// Inicio de la tienda, con sesión o sin cuenta (decisión D49), como una tienda online: el buscador, "Comprá por
// categoría" con sus tarjetas y una fila que se desliza por cada categoría. Con sesión, arriba el saludo, cuándo llega y
// "Lo de siempre"; sin cuenta, una franja para entrar y ver los precios propios.
import { cx, Icono, useToast } from '@reparto/ui';
import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Aviso, Boton, ErrorCarga, TituloSeccion } from '../componentes/base';
import { EsqueletoLoDeSiempre, EsqueletoVitrina } from '../componentes/Esqueletos';
import { EnlacePrivacidad } from './Privacidad';
import { Pantalla } from '../componentes/Marco';
import { iconoDeProducto } from '../componentes/Miniatura';
import { topeDe } from '../componentes/Producto';
import {
  Carrusel,
  CLASES_EN_CARRUSEL,
  CLASES_GRILLA,
  TarjetaProducto,
  TituloFila,
  VerTodos,
} from '../componentes/TarjetaProducto';
import { useVolverAPedir } from '../componentes/VolverAPedir';
import { bultosDe, cargarVarios } from '../datos/carrito';
import { useConsulta } from '../datos/consultas';
import { useCuenta, useNegocio } from '../datos/cuenta';
import { cambiarCarrito, useCarrito } from '../datos/estado-carrito';
import { fechaEntrega, fechaLarga, plural, primerNombre } from '../datos/formato';
import { claveFuente, traerVitrina, useFuente, useReglas } from '../datos/fuente';
import { useMarca } from '../datos/marca';
import { frecuentes } from '../datos/pedidos';
import { useSesion, useVendedor } from '../datos/sesion';
import type { Categoria, Frecuentes, Vitrina } from '../datos/tipos';

/** Tarjetas de categoría en el inicio (las que más productos tienen); el resto, en "Todas las categorías". */
const ACCESOS = 6;

export const rutaCategoria = (nombre: string) => `/categoria/${encodeURIComponent(nombre)}`;

export function Inicio() {
  const fuente = useFuente();
  const conSesion = useSesion((s) => !!s.sesion);
  const reglas = useReglas();
  const vitrina = useConsulta(fuente ? `vitrina:${claveFuente(fuente)}` : null, () => traerVitrina(fuente!));

  return (
    <Pantalla amplia>
      <div className="px-4 pb-8">
        {conSesion ? <Saludo /> : <FranjaVisitante />}
        <BuscadorCatalogo />
        {reglas.aviso ? (
          <Aviso tono="aviso" icono="campaign" className="mt-4">
            {reglas.aviso}
          </Aviso>
        ) : null}
        {conSesion && fuente?.tipo === 'negocio' ? (
          <LoDeSiempre negocioId={fuente.id} permitirSinStock={reglas.permitirSinStock} />
        ) : null}
        {vitrina.cargando ? (
          <EsqueletoVitrina />
        ) : vitrina.error && !vitrina.datos ? (
          <div className="mt-6">
            <ErrorCarga mensaje={vitrina.error} sinRed={vitrina.sinRed} onReintentar={() => void vitrina.recargar()} />
          </div>
        ) : vitrina.datos ? (
          <>
            <ComprarPorCategoria vitrina={vitrina.datos} />
            <Filas vitrina={vitrina.datos} permitirSinStock={reglas.permitirSinStock} />
          </>
        ) : null}
        <PieInicio />
      </div>
    </Pantalla>
  );
}

/** Al final del inicio: de quién es la tienda y la política de privacidad (con los créditos de las fotos). */
function PieInicio() {
  const nombre = useMarca((s) => s.marca?.nombre);
  return (
    <footer className="mt-10 flex flex-col items-center gap-1 border-t border-stone-200 pt-5 text-center text-[15px] text-stone-600">
      {nombre ? <p>Tienda de pedidos de {nombre}</p> : null}
      <EnlacePrivacidad />
    </footer>
  );
}

function Saludo() {
  const perfil = useCuenta((s) => s.perfil)!;
  const negocio = useNegocio();
  const vendedor = useVendedor();
  const navegar = useNavigate();
  const nombre = primerNombre(negocio?.contacto);
  const fecha = perfil.tienda.fechas_entrega[0];
  // Modo vendedor (decisión D60): el pedido es del cliente que visita, con sus precios.
  if (vendedor && negocio)
    return (
      <section className="pt-5" aria-label="Pedido del cliente">
        <p className="text-[15px] font-semibold text-primary-700">Pedido para</p>
        <h1 className="text-[28px] leading-tight font-bold text-stone-900">{negocio.nombre}</h1>
        {fecha ? (
          <p className="mt-1 flex items-start gap-1.5 text-[15px] text-stone-700">
            <Icono nombre="local_shipping" tamano={20} className="mt-px text-primary-700" />
            <span>
              Si lo pide ahora le llega{' '}
              <b className="font-semibold">{fechaEntrega(fecha, perfil.empresa.zona_horaria)}</b>
            </span>
          </p>
        ) : null}
      </section>
    );
  return (
    <section className="pt-5" aria-label="Tu pedido">
      <h1 className="text-[28px] leading-tight font-bold text-stone-900">{nombre ? `Hola, ${nombre}` : 'Hola'}</h1>
      {negocio ? (
        perfil.clientes.length > 1 ? (
          <button
            type="button"
            onClick={() => navegar('/negocio')}
            className="-mx-1 mt-0.5 inline-flex min-h-11 max-w-full items-center gap-1 rounded-xl px-1 text-left text-lg text-stone-700 hover:bg-stone-200/60"
            aria-label={`Pedís para ${negocio.nombre}. Cambiar de negocio`}
          >
            <span className="truncate">{negocio.nombre}</span>
            <Icono nombre="expand_more" />
          </button>
        ) : (
          <p className="mt-0.5 text-lg text-stone-700">{negocio.nombre}</p>
        )
      ) : null}
      {fecha ? (
        <p className="mt-1 flex items-start gap-1.5 text-[15px] text-stone-700">
          <Icono nombre="local_shipping" tamano={20} className="mt-px text-primary-700" />
          <span>
            Pedí ahora y te llega <b className="font-semibold">{fechaEntrega(fecha, perfil.empresa.zona_horaria)}</b>
          </span>
        </p>
      ) : null}
    </section>
  );
}

/** Sin cuenta: una franja chica para entrar (y ver los precios propios). */
function FranjaVisitante() {
  const modo = useMarca((s) => s.marca?.catalogo?.publico);
  const sinPrecios = modo === 'sin_precios';
  return (
    <div className="mt-4 flex items-center gap-3 rounded-2xl border border-primary-200 bg-primary-50 px-4 py-2.5 text-[15px] text-primary-950">
      <Icono nombre={sinPrecios ? 'sell' : 'storefront'} tamano={22} className="shrink-0 text-primary-700" />
      <p className="min-w-0 flex-1 leading-snug">
        {sinPrecios ? 'Entrá o creá tu cuenta para ver los precios.' : '¿Ya sos cliente? Entrá para ver tus precios.'}
      </p>
      <Link
        to="/entrar"
        className="inline-flex min-h-11 shrink-0 items-center rounded-full bg-primary-600 px-4 font-semibold text-sobre-marca hover:brightness-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
      >
        Entrar
      </Link>
    </div>
  );
}

/** "Buscar en el catálogo": busca en todo el catálogo (pantalla Buscar). */
export function BuscadorCatalogo({
  inicial = '',
  alBuscar,
  enfocar,
}: {
  inicial?: string;
  /** Filtra mientras se escribe (la pantalla de la grilla). Sin esto, buscar lleva a la pantalla Buscar. */
  alBuscar?: (q: string) => void;
  /** Con el cursor adentro al abrir (la pantalla Buscar desde la barra de abajo). */
  enfocar?: boolean;
}) {
  const navegar = useNavigate();
  const [texto, setTexto] = useState(inicial);
  const buscar = (ev: FormEvent) => {
    ev.preventDefault();
    const q = texto.trim();
    (document.activeElement as HTMLElement | null)?.blur();
    if (alBuscar) alBuscar(q);
    else navegar(q ? `/buscar?q=${encodeURIComponent(q)}` : '/buscar');
  };
  return (
    <form role="search" onSubmit={buscar} className="mt-4">
      <label className="flex min-h-14 items-center gap-2.5 rounded-2xl bg-white px-4 shadow-sm ring-1 ring-stone-200 focus-within:ring-2 focus-within:ring-primary-600">
        <Icono nombre="search" tamano={24} className="shrink-0 text-stone-500" />
        <span className="sr-only">Buscar en el catálogo</span>
        <input
          id="buscar-catalogo"
          type="search"
          autoFocus={enfocar}
          enterKeyHint="search"
          autoComplete="off"
          placeholder="Buscar en el catálogo"
          value={texto}
          onChange={(e) => {
            setTexto(e.target.value);
            if (alBuscar) alBuscar(e.target.value);
          }}
          className="min-w-0 flex-1 bg-transparent py-3 text-[17px] text-stone-900 placeholder:text-stone-500 focus:outline-none [&::-webkit-search-cancel-button]:hidden"
        />
        {texto ? (
          <button
            type="button"
            onClick={() => {
              setTexto('');
              alBuscar?.('');
            }}
            className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-stone-600 hover:bg-stone-100"
            aria-label="Borrar la búsqueda"
          >
            <Icono nombre="close" />
          </button>
        ) : null}
      </label>
    </form>
  );
}

function ComprarPorCategoria({ vitrina }: { vitrina: Vitrina }) {
  const accesos = [...vitrina.categorias].sort((a, b) => b.articulos - a.articulos).slice(0, ACCESOS);
  return (
    <section className="mt-8" aria-labelledby="por-categoria">
      <div className="flex min-h-11 items-center justify-between gap-3">
        <TituloFila id="por-categoria" detalle={plural(vitrina.total, 'producto', 'productos')}>
          Comprá por categoría
        </TituloFila>
        <VerTodos a="/buscar" etiqueta="Ver todo el catálogo" />
      </div>
      {accesos.length ? (
        <nav aria-label="Categorías" className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-6">
          {accesos.map((c) => (
            <TarjetaCategoria key={c.nombre} c={c} />
          ))}
        </nav>
      ) : null}
      {vitrina.categorias.length > ACCESOS ? (
        <div className="mt-2 flex justify-center">
          <Link
            to="/categorias"
            className="inline-flex min-h-12 items-center gap-2 text-[15px] font-semibold text-primary-700 hover:underline"
          >
            <Icono nombre="grid_view" tamano={20} />
            Todas las categorías ({vitrina.categorias.length})
            <Icono nombre="arrow_forward" tamano={20} />
          </Link>
        </div>
      ) : null}
    </section>
  );
}

export function TarjetaCategoria({ c, conCantidad }: { c: Categoria; conCantidad?: boolean }) {
  return (
    <Link
      to={rutaCategoria(c.nombre)}
      className={cx(
        'flex min-h-24 flex-col items-center justify-center gap-2 rounded-2xl border border-stone-200 bg-white shadow-sm px-2 py-3 text-center text-[15px] leading-tight font-semibold text-stone-900',
        'hover:border-primary-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600',
      )}
    >
      <Icono nombre={iconoDeProducto(c.nombre)} tamano={30} className="text-primary-700" />
      <span className="line-clamp-2">{c.nombre}</span>
      {conCantidad ? (
        <span className="text-sm font-normal text-stone-600">{plural(c.articulos, 'producto', 'productos')}</span>
      ) : null}
    </Link>
  );
}

function Filas({ vitrina, permitirSinStock }: { vitrina: Vitrina; permitirSinStock: boolean }) {
  return (
    <div className="mt-6 space-y-8">
      {vitrina.categorias
        .filter((c) => c.items.length)
        .map((c) => {
          const id = `fila-${c.nombre.replace(/\W+/g, '-')}`;
          return (
            <section key={c.nombre} aria-labelledby={id} className="border-t border-stone-200 pt-5">
              <Carrusel
                nombre={c.nombre}
                titulo={
                  <TituloFila
                    id={id}
                    icono={iconoDeProducto(c.nombre)}
                    detalle={plural(c.articulos, 'producto', 'productos')}
                  >
                    {c.nombre}
                  </TituloFila>
                }
                accion={<VerTodos a={rutaCategoria(c.nombre)} nombre={c.nombre} />}
              >
                {c.items.map((a) => (
                  <li key={a.id} className={CLASES_EN_CARRUSEL}>
                    <TarjetaProducto a={a} permitirSinStock={permitirSinStock} />
                  </li>
                ))}
                {c.articulos > c.items.length ? (
                  <li className={CLASES_EN_CARRUSEL}>
                    <Link
                      to={rutaCategoria(c.nombre)}
                      className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-stone-300 bg-white p-4 text-center font-semibold text-primary-700 hover:border-primary-600 focus-visible:outline-2 focus-visible:outline-primary-600"
                    >
                      <Icono nombre={iconoDeProducto(c.nombre)} tamano={36} />
                      Ver los {c.articulos} productos
                      <Icono nombre="arrow_forward" />
                    </Link>
                  </li>
                ) : null}
              </Carrusel>
            </section>
          );
        })}
    </div>
  );
}

/** Con sesión: lo que más pide, para agregarlo con un toque, y su último pedido para repetirlo. */
export function LoDeSiempre({
  negocioId,
  permitirSinStock,
  grilla,
}: {
  negocioId: string;
  permitirSinStock: boolean;
  /** En su propia pantalla: grilla en vez de una fila que se desliza. */
  grilla?: boolean;
}) {
  const consulta = useConsulta<Frecuentes>(`frecuentes:${negocioId}`, () => frecuentes(negocioId));
  const volver = useVolverAPedir();
  const { mostrar } = useToast();
  const carrito = useCarrito((s) => s.carrito);
  const { datos, cargando, error, sinRed, recargar } = consulta;
  if (cargando) return <EsqueletoLoDeSiempre grilla={grilla} />;
  if (error && !datos)
    return (
      <section className="mt-6">
        <ErrorCarga
          compacto
          mensaje={`No pudimos traer lo de siempre. ${error}`}
          sinRed={sinRed}
          onReintentar={() => void recargar()}
        />
      </section>
    );
  const lista = datos?.datos ?? [];
  if (!lista.length) return null;
  const faltan = lista.filter((a) => a.se_puede_pedir && bultosDe(carrito, a.id) === 0);
  const agregarTodo = () => {
    if (!faltan.length) return mostrar('Ya está todo lo de siempre en tu pedido', { tipo: 'info' });
    cambiarCarrito((c) =>
      cargarVarios(
        c,
        faltan.map((a) => {
          const tope = topeDe(a, permitirSinStock);
          return { ...a, bultos: tope ? Math.min(tope, a.bultos_sugeridos) : a.bultos_sugeridos };
        }),
      ),
    );
    mostrar(`Agregamos ${plural(faltan.length, 'producto', 'productos')} de lo de siempre`, {
      icono: 'add_shopping_cart',
    });
  };
  const ultimo = datos?.ultimo;
  const conocidos = new Map(lista.map((a) => [a.id, a]));
  return (
    <section className={grilla ? 'mt-6' : 'mt-8'} aria-labelledby="lo-de-siempre">
      {grilla ? (
        <>
          <TituloSeccion
            id="lo-de-siempre"
            accion={
              <Boton variante="secundario" icono="playlist_add" onClick={agregarTodo} className="shrink-0">
                Agregar todo
              </Boton>
            }
          >
            Lo de siempre
          </TituloSeccion>
          <ul className={CLASES_GRILLA}>
            {lista.map((a) => (
              <li key={a.id}>
                <TarjetaProducto a={a} permitirSinStock={permitirSinStock} frecuente />
              </li>
            ))}
          </ul>
        </>
      ) : (
        <Carrusel
          nombre="lo de siempre"
          titulo={
            <TituloFila id="lo-de-siempre" icono="replay" detalle={plural(lista.length, 'producto', 'productos')}>
              Lo de siempre
            </TituloFila>
          }
          accion={
            <button
              type="button"
              onClick={agregarTodo}
              className="inline-flex min-h-11 shrink-0 items-center gap-1 rounded-full px-1 text-[15px] font-semibold text-primary-700 hover:underline focus-visible:outline-2 focus-visible:outline-primary-600"
            >
              <Icono nombre="playlist_add" tamano={20} />
              Agregar todo
            </button>
          }
        >
          {lista.map((a) => (
            <li key={a.id} className={CLASES_EN_CARRUSEL}>
              <TarjetaProducto a={a} permitirSinStock={permitirSinStock} frecuente />
            </li>
          ))}
        </Carrusel>
      )}
      {ultimo?.items.length ? (
        <button
          type="button"
          onClick={() =>
            volver.pedir(
              ultimo.items.map((i) => ({
                ...(conocidos.get(i.articulo_id) ?? {}),
                id: i.articulo_id,
                bultos: i.bultos,
              })),
              'tu último pedido',
            )
          }
          className="mt-2 flex w-full items-center gap-3 rounded-2xl border border-stone-200 bg-white shadow-sm p-3 text-left hover:border-primary-600 focus-visible:outline-2 focus-visible:outline-primary-600"
        >
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-primary-50 text-primary-700">
            <Icono nombre="history" tamano={28} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-semibold text-stone-900">Repetir último pedido</span>
            <span className="block text-[15px] text-stone-600">
              El del {fechaLarga(ultimo.fecha_entrega)}: {plural(ultimo.items.length, 'artículo', 'artículos')}
            </span>
          </span>
          <Icono nombre="chevron_right" className="shrink-0 text-stone-500" />
        </button>
      ) : null}
      {volver.hoja}
    </section>
  );
}
